// Convert markdown → clean white-background HTML for clipboard/Word/Docs
// and helpers to download .md / .txt / and call backend for .docx / .pdf

const escapeHtml = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function inlineMd(text) {
  // Links [text](url)
  let s = text.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_, t, u) => `<a href="${u}" style="color:#1A56DB;text-decoration:underline">${escapeHtml(t)}</a>`
  );
  // Bold
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong style="font-weight:700;color:#000">$1</strong>');
  // Italic
  s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
  // Inline code
  s = s.replace(
    /`([^`]+)`/g,
    '<code style="font-family:Consolas,monospace;background:#F1F5F9;color:#1F2937;padding:1px 4px;border-radius:3px;font-size:0.95em">$1</code>'
  );
  return s;
}

export function markdownToCleanHtml(md, title) {
  if (!md) return "";
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  if (title) out.push(`<h1 style="font-family:Helvetica,Arial,sans-serif;color:#0F172A;font-size:22px;margin:0 0 16px">${escapeHtml(title)}</h1>`);

  let i = 0;
  const n = lines.length;
  while (i < n) {
    const line = lines[i];

    // Code fence
    if (line.trim().startsWith("```")) {
      const buf = [];
      i++;
      while (i < n && !lines[i].trim().startsWith("```")) {
        buf.push(lines[i]);
        i++;
      }
      i++;
      out.push(
        `<pre style="background:#F1F5F9;border:1px solid #E2E8F0;border-radius:4px;padding:10px;font-family:Consolas,Courier,monospace;font-size:12px;color:#1F2937;white-space:pre-wrap;overflow:auto">${escapeHtml(buf.join("\n"))}</pre>`
      );
      continue;
    }
    // HR
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      out.push('<hr style="border:none;border-top:1px solid #CBD5E1;margin:14px 0" />');
      i++;
      continue;
    }
    // Heading
    const h = line.match(/^(#{1,4})\s+(.+)$/);
    if (h) {
      const lvl = h[1].length;
      const sizes = { 1: 22, 2: 17, 3: 14, 4: 12 };
      out.push(
        `<h${lvl} style="font-family:Helvetica,Arial,sans-serif;color:#0F172A;font-size:${sizes[lvl]}px;font-weight:700;margin:14px 0 6px">${inlineMd(escapeHtml(h[2]))}</h${lvl}>`
      );
      i++;
      continue;
    }
    // Table
    if (line.includes("|") && i + 1 < n && /^\s*\|?\s*:?-+:?(\s*\|\s*:?-+:?)+\s*\|?\s*$/.test(lines[i + 1])) {
      const header = line.trim().replace(/^\||\|$/g, "").split("|").map((s) => s.trim());
      i += 2;
      const rows = [];
      while (i < n && lines[i].includes("|") && lines[i].trim()) {
        rows.push(lines[i].trim().replace(/^\||\|$/g, "").split("|").map((s) => s.trim()));
        i++;
      }
      const th = header
        .map((h) => `<th style="background:#0F172A;color:#fff;padding:8px;font-family:Helvetica,Arial,sans-serif;font-size:11px;font-weight:600;text-align:left;border:1px solid #0F172A">${inlineMd(escapeHtml(h))}</th>`)
        .join("");
      const tr = rows
        .map((r, ri) => {
          const td = r
            .slice(0, header.length)
            .map((c) => `<td style="padding:8px;border:1px solid #E2E8F0;font-family:Helvetica,Arial,sans-serif;font-size:11px;color:#0F172A;background:${ri % 2 ? "#fff" : "#F8FAFC"}">${inlineMd(escapeHtml(c))}</td>`)
            .join("");
          return `<tr>${td}</tr>`;
        })
        .join("");
      out.push(
        `<table style="border-collapse:collapse;width:100%;margin:8px 0;border:1px solid #E2E8F0"><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table>`
      );
      continue;
    }
    // UL
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < n && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ""));
        i++;
      }
      out.push(
        `<ul style="margin:6px 0;padding-left:22px;color:#0F172A;font-family:Helvetica,Arial,sans-serif;font-size:13px;line-height:1.55">${items
          .map((it) => `<li style="margin:2px 0">${inlineMd(escapeHtml(it))}</li>`)
          .join("")}</ul>`
      );
      continue;
    }
    // OL
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < n && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      out.push(
        `<ol style="margin:6px 0;padding-left:22px;color:#0F172A;font-family:Helvetica,Arial,sans-serif;font-size:13px;line-height:1.55">${items
          .map((it) => `<li style="margin:2px 0">${inlineMd(escapeHtml(it))}</li>`)
          .join("")}</ol>`
      );
      continue;
    }
    // Blank
    if (!line.trim()) {
      i++;
      continue;
    }
    // Paragraph (gather)
    const buf = [line];
    i++;
    while (
      i < n &&
      lines[i].trim() &&
      !/^(#{1,4}\s|\s*[-*+]\s|\s*\d+\.\s|```)/.test(lines[i]) &&
      !/^\s*\|/.test(lines[i])
    ) {
      buf.push(lines[i]);
      i++;
    }
    out.push(
      `<p style="margin:6px 0;color:#0F172A;font-family:Helvetica,Arial,sans-serif;font-size:13px;line-height:1.55">${inlineMd(escapeHtml(buf.join(" ")))}</p>`
    );
  }
  // NOTE: previously wrapped output in <div>. That broke the multi-page paginator
  // in Assistant.jsx (it walked .children which returned the wrapper as a single
  // child). Caller .prose-doc container provides the visual background/typography.
  return out.join("");
}

export function markdownToPlainText(md) {
  if (!md) return "";
  return md
    .replace(/```[\s\S]*?```/g, (s) => s.replace(/^```\w*\n?|```$/g, ""))
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1$2")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)")
    .replace(/^\s*[-*+]\s+/gm, "• ")
    .replace(/^\s*(\d+)\.\s+/gm, "$1. ");
}

export async function copyMarkdownAsRichText(md, plainOverride) {
  const html = markdownToCleanHtml(md);
  const text = plainOverride ?? markdownToPlainText(md);
  if (navigator.clipboard && window.ClipboardItem) {
    try {
      const item = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch (e) {
      // fall through to legacy
    }
  }
  // Fallback: plain text only
  await navigator.clipboard?.writeText?.(text);
  return true;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadMarkdown(md, stem = "acquigine-document") {
  downloadBlob(new Blob([md || ""], { type: "text/markdown;charset=utf-8" }), `${stem}.md`);
}

export function downloadPlainText(md, stem = "acquigine-document") {
  downloadBlob(new Blob([markdownToPlainText(md)], { type: "text/plain;charset=utf-8" }), `${stem}.txt`);
}
